import React, { useRef, useState } from "react";
import { Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, mediaSrc, uploadMedia, dataUrlToBase64, compressImage } from "../api";
import { useApp } from "../context/AppContext";
import { TextOverlays } from "./TextOverlays";

/**
 * Photos of ONE measured opening: the annotated photo (arrows) big, extra shots as thumbnails
 * beside it. The sender can add more extra photos any time with the "+" tile.
 */
export function MeasurePhotos({ main, extras, message, index = null, canAdd, onOpenPhoto, selectMode, lang }) {
  const { user, t } = useApp();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const open = (m) => (e) => { if (!selectMode) { e.stopPropagation(); onOpenPhoto && onOpenPhoto(m, message); } };

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 10);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    try {
      const ids = [];
      for (const f of files) {
        const { dataUrl } = await compressImage(f);
        const media = await uploadMedia({
          base64: dataUrlToBase64(dataUrl), contentType: "image/jpeg", kind: "image", jobId: message.meta?.linked_job_id || null,
          category: "measure_extra", uploadedBy: user.id, filename: `measure-extra-${Date.now()}-${ids.length + 1}.jpg`,
        });
        ids.push(media.id);
      }
      await api.post(`/messages/${message.id}/measurement/extra-photos`, { user_id: user.id, media_ids: ids, index });
      toast.success(t("saved"));
    } catch (err) {
      toast.error(t("something_wrong"));
    } finally {
      setBusy(false);
    }
  };

  const hasSide = (extras && extras.length > 0) || canAdd;
  if (!main && !hasSide) return null;
  return (
    <div className={`flex gap-1.5 mt-1.5 ${hasSide ? "" : ""}`} data-testid="measure-photos">
      {main && (
        <div className="relative cursor-pointer flex-1 min-w-0" data-testid="measure-main-photo" onClick={open(main)}>
          <img src={mediaSrc(main)} alt="" loading="lazy" className="rounded-lg w-full max-h-72 object-cover border" />
          <TextOverlays media={main} lang={lang} size="sm" />
        </div>
      )}
      {hasSide && (
        <div className="flex flex-col gap-1.5 w-14 shrink-0" data-testid="measure-extra-column">
          {(extras || []).map((m) => (
            <img key={m.id} src={mediaSrc(m)} alt="" loading="lazy" onClick={open(m)}
                 className="h-14 w-14 rounded-lg object-cover border cursor-pointer" data-testid="measure-extra-photo" />
          ))}
          {canAdd && (
            <>
              <button type="button" disabled={busy} data-testid="measure-extra-add-later"
                      onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
                      title={t("measure_extra_add_later")}
                      className="h-14 w-14 rounded-lg border-2 border-dashed flex flex-col items-center justify-center text-muted-foreground hover:bg-muted/60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-5 w-5" />}
                <span className="text-[9px] leading-none mt-0.5">{t("add")}</span>
              </button>
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onPick}
                     onClick={(e) => e.stopPropagation()} data-testid="measure-extra-later-input" />
            </>
          )}
        </div>
      )}
    </div>
  );
}
